import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Router } from '@angular/router';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NavigationMenu } from '../routes/route.model';
import { menuRoutes } from '../routes/routes';

/**
 * HomeComponent implements a high-density, compact enterprise ERP navigation hub
 * for warehouse and food & beverage operations, sourcing directly from routes.ts.
 */
@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, TablerIconsModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent {
  /**
   * Operational priority sorting order:
   * 1. GESTIÓN (Ingresos, Egresos, Kardex)
   * 2. COCINA (SKUs, Platos, Menús)
   * 3. CUADRO PICNIC (Cuadro Picnic)
   * 4. REPORTES (Reporte Stock)
   * 5. ASIGNACIÓN DE MENÚS (Asignar Servicios A&B)
   * 6. PROVEEDORES (Proveedores)
   */
  private readonly _menuPriorityOrder = [
    'GESTIÓN',
    'COCINA',
    'CUADRO PICNIC',
    'REPORTES',
    'ASIGNACIÓN DE MENÚS',
    'PROVEEDORES',
  ];

  constructor(private readonly router: Router) {}

  /**
   * Retrieves all system menus from routes.ts ordered by operational priority.
   */
  public get erpMenus(): NavigationMenu[] {
    return [...menuRoutes].sort((a, b) => {
      const idxA = this._menuPriorityOrder.indexOf(a.menu || '');
      const idxB = this._menuPriorityOrder.indexOf(b.menu || '');
      return (idxA >= 0 ? idxA : 99) - (idxB >= 0 ? idxB : 99);
    });
  }

  /**
   * Returns menus with multiple submodules (GESTIÓN and COCINA) for the top balanced row.
   */
  public get multiModuleMenus(): NavigationMenu[] {
    return this.erpMenus.filter((m) => (m.subMenu?.length || 0) > 1);
  }

  /**
   * Returns menus with a single direct submodule (CUADRO PICNIC, REPORTES, ASIGNACIÓN DE MENÚS, PROVEEDORES).
   */
  public get singleModuleMenus(): NavigationMenu[] {
    return this.erpMenus.filter((m) => (m.subMenu?.length || 0) === 1);
  }

  /**
   * Total count of system menus defined in routes.ts.
   */
  public get totalMenusCount(): number {
    return this.erpMenus.length;
  }

  /**
   * Total count of system submodules defined in routes.ts.
   */
  public get totalSubModulesCount(): number {
    return this.erpMenus.reduce(
      (accum, current) => accum + (current.subMenu?.length || 0),
      0,
    );
  }

  /**
   * Resolves Tabler icon name appropriate for each main menu category.
   *
   * @param menuName - Menu category name from routes.ts.
   * @returns Tabler icon string.
   */
  public getMenuIcon(menuName?: string): string {
    switch (menuName) {
      case 'GESTIÓN':
        return 'table';
      case 'CUADRO PICNIC':
        return 'grill';
      case 'REPORTES':
        return 'arrows-right-left';
      case 'COCINA':
        return 'tools-kitchen-2';
      case 'ASIGNACIÓN DE MENÚS':
        return 'a-b-2';
      case 'PROVEEDORES':
        return 'users-group';
      default:
        return 'box';
    }
  }

  /**
   * Resolves CSS modifier class for menu card styling.
   *
   * @param menuName - Menu category name from routes.ts.
   * @returns CSS modifier class name.
   */
  public getMenuThemeClass(menuName?: string): string {
    switch (menuName) {
      case 'GESTIÓN':
        return 'theme-gestion';
      case 'CUADRO PICNIC':
        return 'theme-picnic';
      case 'REPORTES':
        return 'theme-reportes';
      case 'COCINA':
        return 'theme-cocina';
      case 'ASIGNACIÓN DE MENÚS':
        return 'theme-asignacion';
      case 'PROVEEDORES':
        return 'theme-proveedores';
      default:
        return 'theme-default';
    }
  }

  /**
   * Navigates directly to an application route.
   *
   * @param targetRoute - Destination route path.
   */
  public navigateToRoute(targetRoute: string): void {
    if (!targetRoute) return;
    const formattedRoute = targetRoute.startsWith('/')
      ? targetRoute
      : `/${targetRoute}`;
    this.router.navigate([formattedRoute]);
  }
}
